import * as React from 'react';

import { cn } from '../lib/cn';

export interface SubtabItem {
  id: string;
  label: React.ReactNode;
  to?: string;
  href?: string;
  badge?: number | string;
  icon?: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
  disabled?: boolean;
}

export interface SubtabNavProps {
  /** Section title shown at the top of the column. */
  title?: React.ReactNode;
  /** Optional secondary description / breadcrumb under the title. */
  description?: React.ReactNode;
  /** Items in this section. */
  items: SubtabItem[];
  /** Optional footer slot (e.g. workspace switcher, settings link). */
  footer?: React.ReactNode;
  className?: string;
}

/**
 * Permanent secondary-nav column that appears beside the rail.
 * Lists pages within the currently active section.
 *
 * Renders an `<a>` for items with `href`/`to`, a `<button>` otherwise.
 * Pair with `react-router-dom <NavLink>` at the call site for SPA routing.
 */
export const SubtabNav = React.forwardRef<HTMLDivElement, SubtabNavProps>(
  ({ title, description, items, footer, className }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex h-full w-full flex-col',
          'bg-[var(--color-bg)]',
          className,
        )}
      >
        {(title || description) && (
          <div className="px-4 pt-4 pb-3 border-b border-[var(--color-border)]">
            {title ? (
              <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--color-fg-secondary)]">
                {title}
              </div>
            ) : null}
            {description ? (
              <div className="mt-1 text-xs text-[var(--color-fg-muted)]">{description}</div>
            ) : null}
          </div>
        )}

        <ul className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
          {items.map((item) => (
            <li key={item.id}>
              <SubtabItem item={item} />
            </li>
          ))}
        </ul>

        {footer ? (
          <div className="border-t border-[var(--color-border)] p-3">{footer}</div>
        ) : null}
      </div>
    );
  },
);
SubtabNav.displayName = 'SubtabNav';

function SubtabItem({ item }: { item: SubtabItem }) {
  const cls = cn(
    'flex w-full items-center gap-2 rounded-[var(--radius-md)] px-3 py-2',
    'text-sm text-[var(--color-fg-secondary)]',
    'transition-colors duration-150',
    'hover:bg-[var(--color-subtle)] hover:text-[var(--color-fg)]',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]',
    item.active && 'bg-[var(--color-accent-subtle)] text-[var(--color-accent)] font-medium',
    item.disabled && 'opacity-50 pointer-events-none',
  );

  const inner = (
    <>
      {item.icon ? <span className="shrink-0">{item.icon}</span> : null}
      <span className="flex-1 truncate">{item.label}</span>
      {item.badge !== undefined && item.badge !== null && item.badge !== '' ? (
        <span
          className={cn(
            'inline-flex items-center justify-center rounded-full px-1.5 min-w-[18px] h-[18px]',
            'text-[10px] font-semibold',
            item.active
              ? 'bg-[var(--color-accent)] text-white'
              : 'bg-[var(--color-subtle)] text-[var(--color-fg-secondary)]',
          )}
        >
          {item.badge}
        </span>
      ) : null}
    </>
  );

  if (item.href || item.to) {
    return (
      <a href={item.href ?? item.to} data-pop className={cls} aria-current={item.active ? 'page' : undefined}>
        {inner}
      </a>
    );
  }
  return (
    <button type="button" onClick={item.onClick} className={cls} disabled={item.disabled}>
      {inner}
    </button>
  );
}
